import { KatPagination, KatSpinner } from "@amzn/katal-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NestHistoryDateFilter } from "./NestHistoryDateFilter";
import { NestHistoryTable } from "./NestHistoryTable";
import "./index.css";
import React from "react";
import type { NestUseCase, TestStatus } from "src/api/interfaces/nest";
import type { NestTestResult } from "src/api/interfaces/nest/history";
import { useNestHistoryFetcher } from "../../hooks/useNestHistoryFetcher";
import { NestHistoryDetail } from "./NestHistoryDetail";
import { NestPanel } from "../NestPanel";

interface NestHistoryProps {
  useCase: NestUseCase;
  onNewTest: (useCaseId: string) => void;
  onClose?: () => void;
}

type PaginationState = {
  pageIndex: number;
  pageSize: number;
};

export default function NestHistory({
  useCase,
  onNewTest,
  onClose,
}: NestHistoryProps) {
  const { t, i18n } = useTranslation();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 1,
    pageSize: 10,
  });
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [status, setStatus] = useState<TestStatus>();
  const [selectedTest, setSelectedTest] = useState<NestTestResult | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: <When we change to another use case we need to reset everything>
  useEffect(() => {
    setSelectedTest(null);
    setPagination({ pageIndex: 1, pageSize: 10 });
    setDateRange({});
    setStatus(undefined);
  }, [useCase.id]);

  const { data, isLoading } = useNestHistoryFetcher({
    useCaseId: useCase.id,
    submissionDateGte: dateRange.from,
    submissionDateLte: dateRange.to,
    status,
  });

  const allTests = data?.tests ?? [];
  const totalItems = allTests.length;

  if (selectedTest) {
    return (
      <div className="nest-history">
        <NestHistoryDetail
          test={selectedTest}
          onBack={() => setSelectedTest(null)}
          onNewTest={onNewTest}
          onClose={onClose}
        />
      </div>
    );
  }

  return (
    <div className="nest-history">
      <NestPanel
        title={t("aic_nest_history_name", {
          name: t(useCase.name.key, useCase.name.parameters),
        })}
        onClose={onClose}
        closeAriaLabel={t("aic_nest_history_close_label")}
        noPadding
        headerActions={
          <NestHistoryDateFilter
            dateFrom={dateRange.from}
            dateTo={dateRange.to}
            status={status}
            onDateRangeChange={(from, to) => setDateRange({ from, to })}
            onStatusChange={setStatus}
          />
        }
        footer={
          totalItems > 0 ? (
            <KatPagination
              page={pagination.pageIndex}
              totalItems={totalItems}
              itemsPerPage={pagination.pageSize}
              variant="footer"
              onPageChanged={(e) =>
                setPagination({ ...pagination, pageIndex: e.detail.page })
              }
              onItemsPerPageChange={(e) =>
                setPagination({
                  pageIndex: 1,
                  pageSize: e.detail.value,
                })
              }
              className="nest-history-table-pagination"
              locale={i18n.language}
            />
          ) : undefined
        }
      >
        {isLoading ? (
          <KatSpinner />
        ) : totalItems ? (
          <div className="nest-history-table-container">
            <NestHistoryTable
              key={useCase.id}
              data={allTests}
              pageIndex={pagination.pageIndex}
              pageSize={pagination.pageSize}
              onRowClick={setSelectedTest}
              onSortChange={() =>
                setPagination((prev) => ({ ...prev, pageIndex: 1 }))
              }
            />
          </div>
        ) : (
          <div className="nest-history-empty">
            {t("aic_nest_history_noData")}
          </div>
        )}
      </NestPanel>
    </div>
  );
}
